import React, { useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import {
	LicenseDesignation,
	LicenseOption,
	LicenseType,
	Severity,
	SystemRole,
} from "node-library/dist/constants";
import { NeutralColor, PrimaryColor } from "node-library/dist/colors";
import { Card } from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import {
	FrontendCompanyTestCategory,
	FrontendOrganizationLicense,
	FrontendUserInfo,
} from "node-library/dist/portal";
import { KebabDropdownButton } from "../../../common-components/button";
import { DeleteIcon, LicenseIcon } from "../../../common-components/svgIcons";
import AddLicenseModal from "./modal/AddLicenseModal";
import DeleteLicenseConfirmModal from "./modal/deleteLicenseConfirmModal";
import { sendPOSTRequest } from "../../../requestHelper/sendRequest";
import { useSnackbar } from "../../../common-components/toast/showToastMessage";
import {
	RedErrorIcon,
	RedTrashIcon,
} from "../../../common-components/toast/errorIcons";
import {
	hasPermission,
	PermissionMap,
	Permissions,
} from "node-library/dist/permissions";

interface Props {
	orgDetailsSelectedCategory: FrontendCompanyTestCategory;
	organizationLicenses: FrontendOrganizationLicense[];
	setOrganizationLicenses: React.Dispatch<
		React.SetStateAction<FrontendOrganizationLicense[]>
	>;
	organizationId: string;
	user: FrontendUserInfo;
}

export default function UserLicenseInfoSection(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	//license info
	const {
		organizationLicenses,
		setOrganizationLicenses,
		organizationId,
		orgDetailsSelectedCategory,
		user,
	} = props;
	const [deleteLicenseModalOpen, setDeleteLicenseModalOpen] =
		useState<boolean>(false);

	const [newLicenseModalOpen, setNewLicenseModalOpen] =
		useState<boolean>(false);

	const [loading, setLoading] = useState<boolean>(false);

	const [proceedAction, setProceedAction] = useState<() => void>(
		() => () => {}
	);
	const doesUserHaveDeleteAccess: boolean = props.user
		? hasPermission(
				props.user?.role?.permissions as PermissionMap,
				Permissions.licenses.delete
		  )
		: false;

	const addLicense = (newLicense: FrontendOrganizationLicense) => {
		setOrganizationLicenses((prevLicenses) => [
			...prevLicenses, // Spread the previous licenses
			newLicense, // Add the new license at the end
		]);
	};

	// Function to delete an item by licenseId
	async function deleteLicense(licenseId: string) {
		setLoading(true);
		try {
			const response = await sendPOSTRequest("/license/delete-license", {
				licenseId: licenseId,
			});

			setOrganizationLicenses((prevLicenses) =>
				prevLicenses.filter((license) => license.licenseId !== licenseId)
			);

			openSnackbar(
				translation("LicenseDeleted"),
				Severity.ERROR,
				undefined,
				<RedTrashIcon />
			);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		} finally {
			setLoading(false);
			setDeleteLicenseModalOpen(false);
		}
	}

	const licenseOptionCards: Array<React.ReactNode> = organizationLicenses.map(
		(license, index) => {
			return (
				<Card
					className={`card-border-radius-10p card-medium-padding`}
					tabIndex={0}
					key={license.licenseId}
					id={`profile-license-option-card-${index}`}
					sx={{ backgroundColor: "#F3F3F5", height: "160px" }}
				>
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							justifyContent: "space-between",
							height: "100%",
						}}
					>
						<div>
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									gap: "10px",
								}}
							>
								<div
									style={{
										display: "flex",
										flexDirection: "column",
										gap: "5px",
										maxWidth: "80%",
									}}
								>
									<div
										className="typography-caption"
										style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
									>
										{translation("LicenseNo")}
									</div>
									<div className="typography-heading-03 overflow-ellipsis-text">
										{license.licenseNumber}
									</div>
								</div>
								{doesUserHaveDeleteAccess && (
									<KebabDropdownButton
										menuOptions={[
											{
												key: `delete-license-${license.licenseId}`,
												onClick: () => {
													setProceedAction(
														() => () => deleteLicense(license.licenseId)
													);
													setDeleteLicenseModalOpen(true);
												},
												children: (
													<div className="icon-dropdown-menu-item">
														<DeleteIcon />
														{translation("Delete")}
													</div>
												),
											},
										]}
										id={`license-more-action-menu-${license.licenseId}`}
										onClick={(e) => {}}
									></KebabDropdownButton>
								)}
							</div>
						</div>
						<div className="typography-caption profile-license-designation-type-row">
							<div className="overflow-ellipsis-text">
								<div
									className="typography-caption"
									style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
								>
									{translation("LicenseType")}
								</div>
								<div className="typography-body-02">
									{translation(license.licenseType)}
								</div>
							</div>

							<div className="overflow-ellipsis-text">
								<div
									className="typography-caption"
									style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
								>
									{translation("Designation")}
								</div>
								<div className="typography-body-02">
									{translation(license.licenseDesignation)}
								</div>
							</div>
						</div>
					</div>
				</Card>
			);
		}
	);

	const newLicenseCard: React.ReactNode = (
		<Card
			className={`card-border-radius-10p card-medium-padding`}
			tabIndex={0}
			sx={{
				border: `1px solid ${PrimaryColor.PRIMARY_LV5}`,
				height: "160px",
				color: PrimaryColor.PRIMARY_600,
				cursor: "pointer",
				transition: "all 0.3s ease", // Smooth transition
				"&:hover": {
					backgroundColor: PrimaryColor.PRIMARY_50, // Color on hover
				},
				"&:active": {
					backgroundColor: PrimaryColor.PRIMARY_100, // Color when pressed
				},
			}}
			onClick={() => {
				setNewLicenseModalOpen(true);
			}}
		>
			<div
				style={{
					height: "100%",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}
				className="typography-body-01"
			>
				<AddIcon></AddIcon>
				{translation("NewLicense")}
			</div>
		</Card>
	);

	const newLicenseModal: React.ReactNode = (
		<AddLicenseModal
			open={newLicenseModalOpen}
			setOpen={setNewLicenseModalOpen}
			addLicense={addLicense}
			organizationId={organizationId}
			testCategoryId={orgDetailsSelectedCategory.testCategoryId}
		></AddLicenseModal>
	);

	const licenseInformation: React.ReactNode = (
		<Card className="common-card card-normal-padding">
			<div>
				<div className="profile-information-container">
					<div className="profile-icon-header-row typography-heading-03">
						<LicenseIcon></LicenseIcon>
						{translation("LicenseInformation")}
					</div>
					<div
						className="typography-caption"
						style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
					>
						{translation("LicenseInformationDescription")}
					</div>
				</div>

				<div className="profile-license-card-grid">
					{licenseOptionCards} {newLicenseCard}
				</div>
				{newLicenseModal}
			</div>
		</Card>
	);

	return (
		<>
			{licenseInformation}
			{
				<DeleteLicenseConfirmModal
					open={deleteLicenseModalOpen}
					setOpen={setDeleteLicenseModalOpen}
					proceedAction={proceedAction}
				/>
			}
		</>
	);
}
